import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation, useI18nStore } from '../lib/i18n';
import { useAuthStore } from '../lib/auth';
import { useDomain, canRegister } from '../lib/domain';
import { useSystemSettings } from '../hooks/useSystemSettings';
import { useTheme } from '../hooks/useTheme';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Eye, EyeOff, Loader2, Globe, Moon, Sun, CheckCircle, ArrowRight, ArrowLeft, User, Lock, Shield } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LANGUAGES = [
  { code: 'tr', name: 'Türkçe', flag: '\u{1F1F9}\u{1F1F7}' },
  { code: 'en', name: 'English', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'nl', name: 'Nederlands', flag: '\u{1F1F3}\u{1F1F1}' },
  { code: 'fr', name: 'Français', flag: '\u{1F1EB}\u{1F1F7}' }
];

/* ── colour palette ── */
const regPalette = {
  dark: {
    node: [34, 197, 94],
    line: 'rgba(34,197,94,0.10)',
    glow: 'rgba(34,197,94,0.05)',
  },
  light: {
    node: [22, 163, 74],
    line: 'rgba(22,163,74,0.07)',
    glow: 'rgba(22,163,74,0.03)',
  },
};

/* ───── Wave Canvas ───── */
function WaveCanvas({ isDark }) {
  const ref = useRef(null);
  const colRef = useRef(isDark ? regPalette.dark.node : regPalette.light.node);
  useEffect(() => { colRef.current = isDark ? regPalette.dark.node : regPalette.light.node; }, [isDark]);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    let animId;
    let t = 0;

    const resize = () => { c.width = c.offsetWidth; c.height = c.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      const [r, g, b] = colRef.current;
      t += 0.008;

      for (let wave = 0; wave < 4; wave++) {
        ctx.beginPath();
        const amp = 30 + wave * 15;
        const freq = 0.005 - wave * 0.0008;
        const yBase = c.height * 0.3 + wave * (c.height * 0.15);
        const alpha = 0.08 - wave * 0.015;

        for (let x = 0; x <= c.width; x += 2) {
          const y = yBase + Math.sin(x * freq + t + wave * 0.8) * amp + Math.sin(x * freq * 2.3 + t * 1.5) * (amp * 0.3);
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `rgba(${r},${g},${b},${alpha})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      /* floating dots */
      for (let i = 0; i < 30; i++) {
        const x = ((i * 137.508) % c.width);
        const y = (c.height * 0.2) + Math.sin(t + i * 0.5) * c.height * 0.3;
        const glow = 0.15 + Math.sin(t * 2 + i) * 0.1;
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${glow})`;
        ctx.fill();
      }

      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return <canvas ref={ref} className="absolute inset-0 w-full h-full" />;
}

/* ───── Grid Dots ───── */
function GridDots({ isDark }) {
  const color = isDark ? regPalette.dark.line : regPalette.light.line;
  return (
    <div className="absolute inset-0 pointer-events-none"
      style={{ backgroundImage: `radial-gradient(circle, ${color} 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />
  );
}

/* ══════════ MAIN ══════════ */
export const Register = () => {
  const { t, language } = useTranslation();
  const { setLanguage } = useI18nStore();
  const navigate = useNavigate();
  const { register, isLoading } = useAuthStore();
  const { settings } = useSystemSettings();
  const { theme, toggleTheme } = useTheme();
  const domainConfig = useDomain();
  const isDark = theme === 'dark';

  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '', email: '', gsm: '', password: '', password_confirm: '', language
  });
  const [errors, setErrors] = useState({});

  const bgMain = isDark ? 'bg-slate-950' : 'bg-slate-50';
  const textPrimary = isDark ? 'text-white' : 'text-slate-900';
  const textSecondary = isDark ? 'text-slate-400' : 'text-slate-500';
  const cardBg = isDark ? 'bg-slate-900/80 border-slate-800/60' : 'bg-white/90 border-slate-200/60';
  const inputBg = isDark ? 'bg-slate-800/60 border-slate-700 text-white placeholder:text-slate-500' : 'bg-white border-slate-200 text-slate-900';
  const accentColor = isDark ? 'text-emerald-400' : 'text-emerald-600';
  const btnBg = isDark ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-emerald-600 hover:bg-emerald-700';

  const validateStep1 = () => {
    const e = {};
    if (!formData.full_name.trim()) e.full_name = t('validation.required');
    if (!formData.email.trim()) e.email = t('validation.required');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = t('validation.invalidEmail');
    if (!formData.gsm.trim()) e.gsm = t('validation.required');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!formData.password) e.password = t('validation.required');
    else if (formData.password.length < 6) e.password = t('validation.passwordMin');
    if (formData.password !== formData.password_confirm) e.password_confirm = t('validation.passwordMatch');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => { if (step === 1 && validateStep1()) setStep(2); };
  const handleBack = () => { setStep(1); setErrors({}); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;
    const result = await register(formData);
    if (result.success) {
      setSuccess(true);
      toast.success(t('auth.registerSuccess'));
    } else {
      toast.error(result.error);
    }
  };

  /* ── Success Screen ── */
  if (success) {
    return (
      <div className={`min-h-screen ${bgMain} relative overflow-hidden flex items-center justify-center`} data-testid="register-success">
        <WaveCanvas isDark={isDark} />
        <GridDots isDark={isDark} />
        <div className={`relative z-10 w-full max-w-md rounded-2xl border backdrop-blur-xl p-8 shadow-2xl text-center ${cardBg}`}>
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mb-6">
            <CheckCircle className="h-8 w-8 text-emerald-500" />
          </div>
          <h1 className={`text-2xl font-bold mb-3 ${textPrimary}`}>{t('auth.registerSuccessTitle')}</h1>
          <p className={`mb-3 ${textSecondary}`}>{t('auth.accountCreated')}</p>
          <p className={`mb-3 ${textSecondary}`}>{t('auth.firmAssignmentPending')}</p>
          <p className={`mb-6 ${textSecondary}`}>{t('auth.loginAfterApproval')}</p>
          <Link to="/login">
            <Button className={`w-full h-12 ${btnBg} text-white font-medium`} data-testid="register-go-login-btn">
              {t('common.login')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bgMain} relative overflow-hidden flex`} data-testid="register-page">
      {/* Animated Background (visible behind both sides) */}
      <WaveCanvas isDark={isDark} />
      <GridDots isDark={isDark} />

      {/* Left Side - Form */}
      <div className="relative z-10 flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Top Controls */}
          <div className="flex items-center justify-between mb-6">
            <Link to="/login" className={`flex items-center gap-2 text-sm ${textSecondary} hover:${textPrimary} transition-colors`} data-testid="register-back-to-login">
              <ArrowLeft className="h-4 w-4" />
              {t('auth.backToLogin')}
            </Link>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={toggleTheme} className={textSecondary} data-testid="register-theme-toggle">
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className={`gap-2 ${textSecondary}`}>
                    <Globe className="h-4 w-4" />
                    {LANGUAGES.find(l => l.code === language)?.flag}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {LANGUAGES.map(l => (
                    <DropdownMenuItem key={l.code} onClick={() => setLanguage(l.code)} className={language === l.code ? 'bg-emerald-50 dark:bg-emerald-950' : ''}>
                      <span className="mr-2">{l.flag}</span>{l.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Progress */}
          <div className="flex items-center gap-3 mb-6">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= 1 ? `${btnBg} text-white` : isDark ? 'bg-slate-800 text-slate-500' : 'bg-slate-100 text-slate-400'
            }`}>
              {step > 1 ? <CheckCircle className="h-4 w-4" /> : <User className="h-4 w-4" />}
            </div>
            <span className={`text-sm font-medium ${step >= 1 ? textPrimary : textSecondary}`}>{t('auth.step1')}</span>
            <div className={`flex-1 h-0.5 rounded ${step > 1 ? 'bg-emerald-500' : isDark ? 'bg-slate-800' : 'bg-slate-200'}`} />
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= 2 ? `${btnBg} text-white` : isDark ? 'bg-slate-800 text-slate-500' : 'bg-slate-100 text-slate-400'
            }`}>
              <Lock className="h-4 w-4" />
            </div>
            <span className={`text-sm font-medium ${step >= 2 ? textPrimary : textSecondary}`}>{t('auth.step2')}</span>
          </div>

          {/* Card */}
          <div className={`rounded-2xl border backdrop-blur-xl p-8 shadow-2xl ${cardBg}`}>
            <h1 className={`text-2xl font-bold mb-1 ${textPrimary}`}>{t('auth.registerTitle')}</h1>
            <p className={`text-sm mb-6 ${textSecondary}`}>
              {t('auth.hasAccount')}{' '}
              <Link to="/login" className={`font-medium ${accentColor} hover:underline`}>{t('common.login')}</Link>
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {step === 1 && (
                <>
                  <div className="space-y-1.5">
                    <Label className={textSecondary}>{t('auth.fullName')}</Label>
                    <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="John Doe" className={`h-11 ${inputBg} ${errors.full_name ? 'border-red-500' : ''}`} data-testid="register-fullname-input" />
                    {errors.full_name && <p className="text-xs text-red-500">{errors.full_name}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className={textSecondary}>{t('auth.email')}</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="name@company.com" className={`h-11 ${inputBg} ${errors.email ? 'border-red-500' : ''}`} data-testid="register-email-input" />
                    {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className={textSecondary}>{t('auth.gsm')}</Label>
                    <Input type="tel" value={formData.gsm} onChange={(e) => setFormData({ ...formData, gsm: e.target.value })}
                      placeholder="+90 555 123 4567" className={`h-11 ${inputBg} ${errors.gsm ? 'border-red-500' : ''}`} data-testid="register-phone-input" />
                    {errors.gsm && <p className="text-xs text-red-500">{errors.gsm}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className={textSecondary}>{t('auth.language')}</Label>
                    <Select value={formData.language} onValueChange={(v) => setFormData({ ...formData, language: v })}>
                      <SelectTrigger className={`h-11 ${inputBg}`} data-testid="register-language-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LANGUAGES.map(l => <SelectItem key={l.code} value={l.code}>{l.flag} {l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="button" onClick={handleNext} className={`w-full h-11 ${btnBg} text-white font-medium`} data-testid="register-next-btn">
                    {t('common.next')} <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </>
              )}

              {step === 2 && (
                <>
                  <div className="space-y-1.5">
                    <Label className={textSecondary}>{t('auth.password')}</Label>
                    <div className="relative">
                      <Input type={showPassword ? 'text' : 'password'} value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="••••••••" className={`h-11 pr-10 ${inputBg} ${errors.password ? 'border-red-500' : ''}`} data-testid="register-password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className={textSecondary}>{t('auth.passwordConfirm')}</Label>
                    <Input type="password" value={formData.password_confirm}
                      onChange={(e) => setFormData({ ...formData, password_confirm: e.target.value })}
                      placeholder="••••••••" className={`h-11 ${inputBg} ${errors.password_confirm ? 'border-red-500' : ''}`} data-testid="register-password-confirm-input" />
                    {errors.password_confirm && <p className="text-xs text-red-500">{errors.password_confirm}</p>}
                  </div>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={handleBack} className={`flex-1 h-11 ${isDark ? 'border-slate-700 text-slate-300' : 'border-slate-200'}`} data-testid="register-back-btn">
                      <ArrowLeft className="h-4 w-4 mr-2" /> {t('common.back')}
                    </Button>
                    <Button type="submit" className={`flex-1 h-11 ${btnBg} text-white font-medium`} disabled={isLoading} data-testid="register-submit-btn">
                      {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
                      {t('common.register')}
                    </Button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Right Side - Image & Hero */}
      <div className={`hidden lg:flex relative z-10 flex-1 items-center justify-center p-12 ${
        isDark ? 'bg-gradient-to-br from-emerald-950/50 to-slate-950/80' : 'bg-gradient-to-br from-emerald-50/50 to-slate-100/80'
      } backdrop-blur-sm`}>
        <div className="max-w-lg text-center">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6 ${
            isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-700'
          }`}>
            <Shield className="h-4 w-4" />
            {t('auth.registerRightTitle')}
          </div>
          <h2 className={`text-3xl font-bold mb-4 ${textPrimary}`}>{t('auth.registerRightTitle')}</h2>
          <p className={`text-lg mb-8 ${textSecondary}`}>{t('auth.registerRightDesc')}</p>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1760611656007-f767a8082758?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600"
              alt="Team collaboration"
              className="rounded-2xl shadow-2xl w-full object-cover max-h-80"
              loading="lazy"
            />
            <div className={`absolute inset-0 rounded-2xl ${isDark ? 'bg-gradient-to-t from-slate-950/40 to-transparent' : 'bg-gradient-to-t from-white/20 to-transparent'}`} />
          </div>
        </div>
      </div>

      {/* Mobile Footer */}
      <div className={`lg:hidden absolute bottom-0 left-0 right-0 z-10 text-center pb-4 text-xs ${textSecondary} flex items-center justify-center gap-2`}>
        <Lock className="h-3 w-3" />
        {t('auth.twoFASecureConnection')}
      </div>
    </div>
  );
};

export default Register;
